import { useEffect, useMemo, type ReactNode } from 'react';
import { useManifestLink } from '../pwa/manifestLink';
import { usePersistedState } from '../pwa/usePersistedState';
import { SCREEN_DOMAINS, type ScreenId } from '../protocol/types';
import { DisplayContext, type DisplayCtx } from './DisplayContext';
import { DisplaySocket } from './DisplaySocket';
import { startLiveWeather } from './liveWeather';
import { DisplayStore } from './store';

export function DisplayProvider({
  screen,
  deviceKey,
  children,
}: {
  screen: ScreenId;
  deviceKey: string;
  children: ReactNode;
}) {
  const ctx = useMemo<DisplayCtx>(() => {
    const store = new DisplayStore();
    const socket = new DisplaySocket({
      deviceKey,
      screen,
      domains: SCREEN_DOMAINS[screen],
      store,
      wsUrlOverride: import.meta.env.VITE_WS_URL || undefined,
    });
    return { store, socket };
  }, [screen, deviceKey]);

  usePersistedState(ctx.store, screen);
  useManifestLink(screen);

  useEffect(() => {
    ctx.socket.start();
    return () => ctx.socket.stop();
  }, [ctx]);

  // Meteo vero dall'app, finché non arriva dal backend (solo se attivato e se lo schermo lo usa).
  useEffect(() => {
    if (!SCREEN_DOMAINS[screen].includes('weather')) return;
    return startLiveWeather({
      store: ctx.store,
      enabled: import.meta.env.VITE_LIVE_WEATHER === 'true',
      lat: Number(import.meta.env.VITE_SITE_LAT ?? 40.85),
      lon: Number(import.meta.env.VITE_SITE_LON ?? 14.27),
    });
  }, [ctx, screen]);

  return <DisplayContext.Provider value={ctx}>{children}</DisplayContext.Provider>;
}
